import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useGlobalContext } from '../../context/GlobalContext';
import { TOOL_ID } from '../../actions';
import { getAdminValidationDashboardAction } from '../../actions/questionsActions';
import AdminDashboard from './AdminDashboard';
import AdminValidationDashboard from './AdminValidationDashboard/AdminValidationDashboard';
import MemberDashboard from './MemberDashboard/MemberDashboard';
import OwnerDashboard from './OwnerDashboard/OwnerDashboard';
import { DASHBOARD_VIEWS, getStoredDashboardView, persistDashboardView, resolveDashboardView } from './dashboardViews';
import roleStyles from './RoleDashboard.module.scss';
import validationStyles from './AdminValidationDashboard/AdminValidationDashboard.module.scss';

export default function Dashboard() {
  const [context, dispatch] = useGlobalContext();
  const location = useLocation();
  const history = useHistory();
  const { userAuth } = context.auth;
  const role = userAuth?.tools?.find((t) => t.tool === TOOL_ID)?.role;
  const divisions = useMemo(() => userAuth?.division || [], [userAuth?.division]);

  const [validationDashboard, setValidationDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(role === 'Admin');

  const loadValidation = useCallback(async () => {
    if (role !== 'Admin') {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const dashboard = await getAdminValidationDashboardAction(dispatch);
    setValidationDashboard(dashboard);
    setIsLoading(false);
  }, [dispatch, role]);

  useEffect(() => {
    loadValidation();
  }, [loadValidation]);

  const requestedView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view');
  }, [location.search]);

  const isValidator = Boolean(validationDashboard?.isValidator);
  const hasPendingValidation = Boolean(validationDashboard?.hasPendingValidation);
  const storedView = getStoredDashboardView(userAuth?._id);

  const { view, accessibleViews } = useMemo(() => resolveDashboardView({
    requestedView,
    storedView,
    role,
    divisions,
    isValidator,
    hasPendingValidation,
  }), [requestedView, storedView, role, divisions, isValidator, hasPendingValidation]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    persistDashboardView(userAuth?._id, view);
  }, [isLoading, userAuth?._id, view]);

  function handleViewChange(nextView) {
    history.replace(`/dashboard?view=${nextView}`);
  }

  const headerProps = {
    views: accessibleViews,
    currentView: view,
    onChange: handleViewChange,
  };

  if (isLoading) {
    return <div className={`page-container ${roleStyles.container} ${validationStyles.page}`} />;
  }

  if (view === DASHBOARD_VIEWS.MEMBER) {
    return <MemberDashboard headerProps={headerProps} />;
  }

  if (view === DASHBOARD_VIEWS.OWNER) {
    return <OwnerDashboard headerProps={headerProps} />;
  }

  if (view === DASHBOARD_VIEWS.VALIDATION) {
    return (
      <AdminValidationDashboard
        dashboard={validationDashboard || undefined}
        headerProps={headerProps}
      />
    );
  }

  if (view === DASHBOARD_VIEWS.ANALYTICS) {
    return <AdminDashboard headerProps={headerProps} />;
  }

  return <MemberDashboard headerProps={headerProps} />;
}
